/**
 * Phase 6 — Workflow-aware KPI tile grid.
 *
 * Renders one card per `kpi_role` declared on the bound workflow's
 * states. Reads /api/v2/workflows/{id}/kpi-summary so the tiles
 * automatically reflect whatever workflow the tenant has bound — no
 * hardcoded TOCA byte values, no per-customer code.
 *
 * ADR-013 / ADR-015 posture:
 *  - The endpoint already 404s for SGS (IndustryType=Asset) and when
 *    AppSettings:Workflows:Enabled is false. The component renders
 *    nothing in either case.
 *  - When the workflow is TOCA_DEFAULT (every state has kpiRole=null)
 *    the API returns an empty tiles array; the component renders
 *    nothing and the legacy TOCA dashboard tiles below it stay
 *    authoritative.
 *
 * See Docs/Features/DynamicFlows/14-reporting-and-dashboards.md.
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  CheckCircle2,
  CircleDashed,
  Hourglass,
  PackageCheck,
  Ban,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useWorkflowKpiSummary, type KpiTile } from '@/api/hooks/useWorkflows';

interface Props {
  /** Bound workflow definition ID for the current tenant + AssetClass scope. */
  workflowDefinitionId: number | null;
  /** Optional client filter — same semantics as the rest of the dashboard. */
  clientId?: number | null;
  /** Auto-refresh interval (ms). 60s by default matches existing tile cadence. */
  refetchIntervalMs?: number;
}

/**
 * Icon and Tailwind tint per controlled-vocabulary role. Unknown
 * (`CUSTOM_*`) roles fall back to a neutral pill.
 */
function visualsFor(role: string) {
  switch (role) {
    case 'PENDING_WORK':
      return { Icon: AlertCircle, tint: 'text-amber-700 bg-amber-50 border-amber-200' };
    case 'IN_PROGRESS':
      return { Icon: Hourglass, tint: 'text-blue-700 bg-blue-50 border-blue-200' };
    case 'COMPLETED_WORK':
      return { Icon: CheckCircle2, tint: 'text-emerald-700 bg-emerald-50 border-emerald-200' };
    case 'AVAILABLE':
      return { Icon: PackageCheck, tint: 'text-slate-700 bg-slate-50 border-slate-200' };
    case 'BLOCKED':
      return { Icon: Ban, tint: 'text-rose-700 bg-rose-50 border-rose-200' };
    default:
      return { Icon: CircleDashed, tint: 'text-zinc-700 bg-zinc-50 border-zinc-200' };
  }
}

export function WorkflowKpiCardGrid({
  workflowDefinitionId,
  clientId = null,
  refetchIntervalMs = 60_000,
}: Props) {
  const { t } = useTranslation('workflows');
  const { data, isLoading, isError } = useWorkflowKpiSummary(workflowDefinitionId, {
    clientId,
    refetchInterval: refetchIntervalMs,
  });

  // Don't render anything until a workflow is bound. The dashboard's
  // legacy tile grid below this component remains the only surface.
  if (workflowDefinitionId == null) return null;

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
    );
  }

  if (isError) {
    // Quiet failure: the API returns 404 for SGS and when the feature
    // flag is off. Either case is correct, not an error to surface.
    return null;
  }

  const tiles = data?.tiles ?? [];
  if (tiles.length === 0) return null;

  return (
    <section
      aria-label={t('kpi.title')}
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
    >
      {tiles.map((tile) => (
        <WorkflowKpiTile key={tile.kpiRole} tile={tile} />
      ))}
    </section>
  );
}

function WorkflowKpiTile({ tile }: { tile: KpiTile }) {
  const navigate = useNavigate();
  const { t } = useTranslation('workflows');
  const { Icon, tint } = visualsFor(tile.kpiRole);
  // Use the translated KPI-role label when the backend gave us only the
  // controlled-vocabulary code; otherwise honour the tenant's
  // kpiAggregationLabel override which the backend already returns.
  const translatedLabel = tile.label === tile.kpiRole
    ? t(`kpi.roles.${tile.kpiRole}`, { defaultValue: tile.label })
    : tile.label;
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => navigate(tile.drilldownUrl)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(tile.drilldownUrl);
        }
      }}
      className={`cursor-pointer transition hover:shadow-md ${tint}`}
    >
      <CardContent className="p-4 flex items-center gap-3">
        <Icon className="h-6 w-6 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-2xl font-semibold leading-tight">{tile.count}</div>
          <div className="text-xs uppercase tracking-wide opacity-80 truncate">
            {translatedLabel}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
